import { Section, Container, Eyebrow, Reveal } from "../components/site/primitives";
import { ProcessSteps, BenefitGrid, CTABanner } from "../components/site/blocks";
import { PROCESS_IMG } from "../lib/content";
import Seo from "../components/site/Seo";

const steps = [
  {
    tag: "Step 1 — Detection",
    title: "Surface Tax Debt Early, Before It Disrupts the Journey",
    body: "Identify customers with IRS tax liabilities at critical lifecycle moments — before transactions stall, outcomes fail, or customers are lost to competitors.",
    points: ["Filing and balance-due scenarios", "Lending and underwriting workflows", "Financial planning and advisory moments"],
  },
  {
    tag: "Step 2 — Engagement",
    title: "We Engage at the Moment It Matters",
    body: "When tax debt becomes a blocker, Community Tax is introduced seamlessly as a behind-the-scenes solution. We step in solely to resolve the debt — preserving the relationship and keeping the deal on track.",
    points: ["Embedded within your experience", "Continuity and trust preserved", "Full visibility into case status"],
  },
  {
    tag: "Step 3 — Resolution",
    title: "We Handle the IRS. You Stay Focused on Your Core Business.",
    body: "Our attorneys, CPAs, and enrolled agents manage the full representation process — from investigation through negotiation and resolution.",
    points: ["All IRS communication and representation", "Investigation, negotiation, and resolution", "Ongoing monitoring, when applicable"],
  },
  {
    tag: "Step 4 — Continuation",
    title: "Customer Journeys Continue With You",
    body: "With the debt resolved, customers move forward with you — completing transactions, achieving their intended outcomes, and maintaining their relationship with your business.",
    points: ["IRS tax debt resolved", "Journeys continue and transactions complete", "Revenue and long-term value preserved"],
  },
];

const integrationModels = [
  { icon: "LayoutGrid", label: "Embedded Workflow", body: "Resolution built directly into your platform experience." },
  { icon: "Plug", label: "API / Integration Layer", body: "Deeper lifecycle and system integration." },
  { icon: "Tag", label: "White-Labeled Solution", body: "Tax debt representation under your brand, powered by Community Tax." },
  { icon: "Share2", label: "Referral Infrastructure", body: "The fastest path to launch, using a proven, trackable model." },
];

export default function HowItWorks() {
  return (
    <>
      <Seo
        title="How It Works | Community Tax Embedded Tax Debt Resolution"
        description="A four-step process — detection, engagement, resolution, continuation — plus flexible integration models built around your architecture."
        path="/how-it-works"
      />
      <section id="our-process" className="pt-10 pb-10 lg:pt-14 lg:pb-12">
        <Container>
          <Eyebrow>Our Process</Eyebrow>
          <Reveal>
            <h1 className="font-display font-extrabold text-[#1E1E24] text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight max-w-4xl">
              Tax Debt, Resolved as a Seamless Part of Your Workflow
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <img
              src={PROCESS_IMG}
              alt="A blocked customer journey becoming unblocked across four stages: detection, engagement, resolution, and continuation"
              className="mt-8 w-full h-[300px] md:h-[400px] lg:h-[460px] object-cover rounded-[28px] ct-shadow-card"
            />
          </Reveal>
        </Container>
      </section>

      <section className="bg-white py-14 lg:py-20">
        <Container>
          <ProcessSteps steps={steps} />
        </Container>
      </section>

      <Section id="integration-models">
        <Eyebrow>Integration Models</Eyebrow>
        <Reveal>
          <h2 className="font-display font-extrabold text-[#1E1E24] text-3xl md:text-4xl lg:text-5xl leading-tight max-w-3xl mb-14">
            Flexible Integration Built Around Your Architecture
          </h2>
        </Reveal>
        <BenefitGrid items={integrationModels} cols={2} />
        <Reveal delay={0.1}>
          <p className="mt-12 text-[#40424D] italic text-base max-w-2xl">
            Every option delivers the same fully managed execution and compliance discipline.
          </p>
        </Reveal>
      </Section>

      <CTABanner />
    </>
  );
}
